import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const databaseService = serviceHost.getDatabaseService();

const promoCodeRepository = repositoryHost.getPromoCodeRepository(databaseService);

type CreatePromoCodeParams = Parameters<typeof promoCodeRepository.create>[0];
type UpdatePromoCodeParams = Parameters<typeof promoCodeRepository.update>[0];
type DeletePromoCodeParams = Parameters<typeof promoCodeRepository.delete>[0];

export const useCreatePromoCode = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<string, Error, CreatePromoCodeParams>({
    mutationKey: ["promo-code", "create"],
    mutationFn: async (params: CreatePromoCodeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to create promo codes",
        );
      }
      return promoCodeRepository.set({ id: params.data.code.toUpperCase(), data: params.data }).then(() => params.data.code.toUpperCase());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promo-codes", "get"] });
    },
  });
};

export const usePromoCodes = () => {
  return useQuery({
    queryKey: ["promo-codes", "get"],
    queryFn: async () =>
      await promoCodeRepository.getAll({
        orderBy: {
          field: "code",
          direction: "asc",
        },
      }),
  });
};

export const usePromoCodeById = (id: string) => {
  return useQuery({
    queryKey: ["promo-code", "id", id],
    queryFn: () => promoCodeRepository.get({ id }),
    enabled: !!id,
  });
};

export const useUpdatePromoCode = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, UpdatePromoCodeParams>({
    mutationKey: ["promo-codes", "update"],
    mutationFn: async (params: UpdatePromoCodeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to update promo codes",
        );
      }
      return promoCodeRepository.update(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promo-codes", "get"] });
      queryClient.invalidateQueries({ queryKey: ["promo-code", "id"] });
    },
  });
};

export const useDeletePromoCode = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, DeletePromoCodeParams>({
    mutationKey: ["promo-code", "delete"],
    mutationFn: async (params: DeletePromoCodeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to delete promo codes",
        );
      }
      return promoCodeRepository.delete(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promo-codes", "get"] });
    },
  });
};
